export function isMp3File(file: File) {
    const lowerName = file.name.toLowerCase();
    return file.type === "audio/mpeg" || lowerName.endsWith(".mp3");
}

export function hasAtLeastOneMedia(
    text: string,
    imageUrl: string | null | undefined,
    audioUrl: string | null | undefined,
    imageFile?: File | null,
    audioFile?: File | null,
) {
    return (
        Boolean(String(text ?? "").trim()) ||
        Boolean(imageUrl) ||
        Boolean(audioUrl) ||
        Boolean(imageFile) ||
        Boolean(audioFile)
    );
}

type QuizAnswerLike = {
    text: string;
    imageUrl: string | null;
    audioUrl: string | null;
    imageFile: File | null;
    audioFile: File | null;
};

type QuizQuestionLike = {
    text: string;
    imageUrl: string | null;
    audioUrl: string | null;
    imageFile: File | null;
    audioFile: File | null;
    answers: QuizAnswerLike[];
};

export function areQuizQuestionsSaveReady(questions: QuizQuestionLike[]) {
    if (!Array.isArray(questions) || questions.length === 0) {
        return false;
    }

    return questions.every((question) => {
        if (!hasAtLeastOneMedia(
            question.text,
            question.imageUrl,
            question.audioUrl,
            question.imageFile,
            question.audioFile,
        )) {
            return false;
        }

        if (!Array.isArray(question.answers) || question.answers.length === 0) {
            return false;
        }

        return question.answers.every((answer) =>
            hasAtLeastOneMedia(
                answer.text,
                answer.imageUrl,
                answer.audioUrl,
                answer.imageFile,
                answer.audioFile,
            ),
        );
    });
}

type MatchingItemLike = {
    keyword: string;
    matchingWord: string;
    imageKeywordUrl: string | null;
    audioKeywordUrl: string | null;
    imageKeywordFile: File | null;
    audioKeywordFile: File | null;
    imageMatchingUrl: string | null;
    audioMatchingUrl: string | null;
    imageMatchingFile: File | null;
    audioMatchingFile: File | null;
};

export function areMatchingItemsSaveReady(items: MatchingItemLike[]) {
    if (!Array.isArray(items) || items.length === 0) {
        return false;
    }

    return items.every((item) =>
        hasAtLeastOneMedia(
            item.keyword,
            item.imageKeywordUrl,
            item.audioKeywordUrl,
            item.imageKeywordFile,
            item.audioKeywordFile,
        ) &&
        hasAtLeastOneMedia(
            item.matchingWord,
            item.imageMatchingUrl,
            item.audioMatchingUrl,
            item.imageMatchingFile,
            item.audioMatchingFile,
        ),
    );
}

type WhackAMoleItemLike = {
    correctAnswer: string;
    wrongAnswer: string;
    imageCorrectAnswerUrl: string | null;
    soundCorrectAnswerUrl: string | null;
    imageCorrectAnswerFile: File | null;
    soundCorrectAnswerFile: File | null;
    imageWrongAnswerUrl: string | null;
    soundWrongAnswerUrl: string | null;
    imageWrongAnswerFile: File | null;
    soundWrongAnswerFile: File | null;
};

export function areWhackAMoleItemsSaveReady(items: WhackAMoleItemLike[]) {
    if (!Array.isArray(items) || items.length === 0) {
        return false;
    }

    return items.every((item) =>
        hasAtLeastOneMedia(
            item.correctAnswer,
            item.imageCorrectAnswerUrl,
            item.soundCorrectAnswerUrl,
            item.imageCorrectAnswerFile,
            item.soundCorrectAnswerFile,
        ) &&
        hasAtLeastOneMedia(
            item.wrongAnswer,
            item.imageWrongAnswerUrl,
            item.soundWrongAnswerUrl,
            item.imageWrongAnswerFile,
            item.soundWrongAnswerFile,
        ),
    );
}

type AnagramItemLike = {
    word: string;
    swapWord: string[];
    imageWordUrl: string | null;
    audioWordUrl: string | null;
    imageWordFile: File | null;
    audioWordFile: File | null;
    imageSwapUrl: string | null;
    audioSwapUrl: string | null;
    imageSwapFile: File | null;
    audioSwapFile: File | null;
};

export function areAnagramItemsSaveReady(items: AnagramItemLike[]) {
    if (!Array.isArray(items) || items.length === 0) {
        return false;
    }

    return items.every((item) => {
        const swapText = item.swapWord.map((entry) => String(entry)).join("").trim();

        return (
            hasAtLeastOneMedia(
                item.word,
                item.imageWordUrl,
                item.audioWordUrl,
                item.imageWordFile,
                item.audioWordFile,
            ) &&
            hasAtLeastOneMedia(
                swapText,
                item.imageSwapUrl,
                item.audioSwapUrl,
                item.imageSwapFile,
                item.audioSwapFile,
            )
        );
    });
}

type CompleteTheSentenceItemLike = {
    sentenceText: string;
    imageQuestionUrl: string | null;
    audioQuestionUrl: string | null;
    imageQuestionFile: File | null;
    audioQuestionFile: File | null;
};

export function areCompleteTheSentenceItemsSaveReady(items: CompleteTheSentenceItemLike[]) {
    if (!Array.isArray(items) || items.length === 0) {
        return false;
    }

    return items.every((item) =>
        hasAtLeastOneMedia(
            item.sentenceText,
            item.imageQuestionUrl,
            item.audioQuestionUrl,
            item.imageQuestionFile,
            item.audioQuestionFile,
        ),
    );
}
